import React from "react";
import "./index.css"
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import Login from "./components/login";
import Register from "./components/register";
import Home from "./components/home";
import Profile from "./components/profile";
import Publish from "./components/publish";
import Edit from "./components/edit";
import ChatBox from "./components/chatbox";
import Chats from "./components/chats";
import Terms from "./components/terms";
import Ver from "./components/ver";
import Cars from "./components/myCars";
import Policy from "./components/policy";
import Support from "./components/support";
import Membership from "./components/membership";
import Header3 from "./components/Header3";
import SideNav from "./components/SideNav";
import { ChatNotificationsProvider } from "./components/ChatNotifications";
import VerifyEmail from "./components/VerifyEmail";
import CompleteProfile from "./components/CompleteProfile";
import InstallPrompt from "./components/InstallPrompt";
import { UserProfileProvider, useUserProfile } from "./components/UserProfile";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./firebase";
import { endSession } from "./session";

// Componente de carga
const Loading = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

// Rutas que requieren sesión iniciada, email verificado y datos de la cuenta completos
const ProtectedRoute = ({ children }) => {
  const [user, loading] = useAuthState(auth);
  const { loading: profileLoading, isComplete } = useUserProfile();

  if (loading) {
    return <Loading />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.emailVerified) {
    return <VerifyEmail />;
  }

  if (profileLoading) {
    return <Loading />;
  }

  if (!isComplete) {
    return <CompleteProfile />;
  }

  return children;
};

const App = () => {
  const [user, loading] = useAuthState(auth);

  const handleLogout = async () => {
    try {
      await endSession();
    } catch {
      // Error ignorado intencionalmente
    }
  };

  return (
    <Router>
      <UserProfileProvider>
        <ChatNotificationsProvider>
          <SideNav />
          <InstallPrompt />
          {loading ? (
            <Loading />
          ) : (
            <Routes>
              {/* Rutas públicas */}
              <Route
                path="/"
                element={user ? <Navigate to="/home" replace /> : <Login />}
              />
              <Route path="/login" element={user ? <Navigate to="/home" replace /> : <Login />} />
              <Route path="/register" element={user ? <Navigate to="/home" replace /> : <Register />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/policy" element={<Policy />} />

              {/* Rutas protegidas */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <Home handleLogout={handleLogout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ver/:carId"
                element={
                  <ProtectedRoute>
                    <Ver />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chatbox/:chatId/:carId"
                element={
                  <ProtectedRoute>
                    <ChatBox />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/publish"
                element={
                  <ProtectedRoute>
                    <Publish />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chats"
                element={
                  <ProtectedRoute>
                    <Chats />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Header3"
                element={
                  <ProtectedRoute>
                    <Header3 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit"
                element={
                  <ProtectedRoute>
                    <Edit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/membership"
                element={
                  <ProtectedRoute>
                    <Membership />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <Support />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/myCars"
                element={
                  <ProtectedRoute>
                    <Cars />
                  </ProtectedRoute>
                }
              />
            </Routes>
          )}
        </ChatNotificationsProvider>
      </UserProfileProvider>
    </Router>
  );
};

export default App;